import React, { useState, useEffect } from 'react';
import type { PageItem, FieldItem } from '../../types';
import { ApiClient } from '../../api/client';
import { useLayout } from '../../context/LayoutContext';
import { TrashModal } from './TrashModal';
import { ArchiveModal } from './ArchiveModal';
import { ArchiveDialog } from './ArchiveDialog';
import {
  Sliders, Plus, Trash2, Eye, ArrowRight, Save, GripVertical,
  CheckCircle, RefreshCw, X, Globe, Archive
} from 'lucide-react';

const ICON_PRESETS = [
  '📋', '🏛️', '🔗', '📰', '📊', '📁', '📂', '📌', '🔒', '📜',
  '💼', '🏢', '⚖️', '📣', '🔔', '✅', '📅', '💰', '🌐', '👥'
];

export const PagesAdminView: React.FC = () => {
  const { setActiveCategory, setActiveModule, setActivePageId } = useLayout();
  const [pages, setPages] = useState<PageItem[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<number | null>(null);
  const [fields, setFields] = useState<FieldItem[]>([]);
  const [isLoadingPages, setIsLoadingPages] = useState(true);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [isSavingMeta, setIsSavingMeta] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Edit Meta Form State
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('📋');
  const [active, setActive] = useState(true);
  const [published, setPublished] = useState(false);
  const [displayMode, setDisplayMode] = useState<'accordion' | 'table' | 'cards' | 'list'>('accordion');

  // New Page Modal
  const [isNewPageModalOpen, setIsNewPageModalOpen] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageSlug, setNewPageSlug] = useState('');
  const [newPageDesc, setNewPageDesc] = useState('');
  const [newPageIcon, setNewPageIcon] = useState('📋');
  const [isCreatingPage, setIsCreatingPage] = useState(false);

  // New Field Modal
  const [isNewFieldModalOpen, setIsNewFieldModalOpen] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [newFieldPlaceholder, setNewFieldPlaceholder] = useState('');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldOptions, setNewFieldOptions] = useState('');
  const [isCreatingField, setIsCreatingField] = useState(false);

  // Preview Modal
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // Batch deletion state
  const [selectedBatchPageIds, setSelectedBatchPageIds] = useState<Set<number>>(new Set());

  const loadPages = async () => {
    setIsLoadingPages(true);
    const res = await ApiClient.getPages(true); // include all pages (active & inactive)
    if (res.ok && res.data) {
      setPages(res.data);
      if (res.data.length === 0) {
        setSelectedPageId(null);
      } else if (!selectedPageId) {
        selectPage(res.data[0]);
      } else {
        const found = res.data.find(p => p.id === selectedPageId);
        if (found) selectPage(found);
        else selectPage(res.data[0]);
      }
    }
    setIsLoadingPages(false);
  };

  const selectPage = (page: PageItem) => {
    setSelectedPageId(page.id);
    setTitle(page.title);
    setSlug(page.slug);
    setDescription(page.description || '');
    setIcon(page.icon || '📋');
    setActive(page.active !== 0);
    setPublished(page.published === 1);
    setDisplayMode(page.display_mode || 'table');
    loadFields(page.id);
  };

  const [fieldSyncMsg, setFieldSyncMsg] = useState<string | null>(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  // archiválás párbeszédablak: egy vagy több (kijelölt) oldal
  const [archiveTarget, setArchiveTarget] = useState<{ ids: number[]; label?: string } | null>(null);
  const loadFields = async (pageId: number) => {
    setIsLoadingFields(true);
    const res = await ApiClient.getPageFields(pageId);
    if (res.ok && res.data) {
      setFields(res.data);
    }
    setIsLoadingFields(false);
  };

  useEffect(() => {
    loadPages();
  }, []);

  const selectedPage = pages.find(p => p.id === selectedPageId) || null;

  const handleSaveMeta = async () => {
    if (!selectedPageId) return;
    setIsSavingMeta(true);
    setSuccessMessage(null);

    const res = await ApiClient.updatePage(selectedPageId, {
      title,
      description,
      icon,
      active: active ? 1 : 0,
      published: published ? 1 : 0,
      display_mode: displayMode,
    });

    if (res.ok) {
      setSuccessMessage('Oldal beállításai sikeresen elmentve!');
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadPages();
    } else {
      alert(res.error || 'Hiba a mentés során');
    }
    setIsSavingMeta(false);
  };

  // Védelmi lánc: Törvényi fix oldalak védettek; egyedi és importált oldalak rekordjaikkal együtt törölhetők.
  const handleDeletePage = async () => {
    if (!selectedPage) return;
    if (selectedPage.is_fixed) {
      alert('A jogszabályi fix törzsoldal védett, nem törölhető!');
      return;
    }

    const recCount = selectedPage.record_count ?? 0;
    const confirmMsg = recCount > 0
      ? `Biztosan törölni szeretné a(z) "${selectedPage.title}" menüpontot és a hozzá tartozó ${recCount} db rekordot és meződefiníciót?\n\nAz oldal a teljes tartalmával a Lomtárba kerül, ahonnan 30 napig visszaállítható.`
      : `Biztosan törölni szeretné a(z) "${selectedPage.title}" menüpontot?\n\nAz oldal a Lomtárba kerül, ahonnan 30 napig visszaállítható.`;

    if (!confirm(confirmMsg)) {
      return;
    }

    setIsSavingMeta(true);
    try {
      const res = await ApiClient.deletePage(selectedPage.id);
      if (res.ok) {
        setSelectedBatchPageIds(prev => {
          const next = new Set(prev);
          next.delete(selectedPage.id);
          return next;
        });
        setSelectedPageId(null);
        await loadPages();
      } else {
        alert(res.error || 'Hiba a törlés során');
      }
    } catch (err: any) {
      alert(err?.message || 'Hálózati hiba a törlés során');
    } finally {
      setIsSavingMeta(false);
    }
  };

  const deletablePages = pages.filter(p => !p.is_fixed);

  const handleBulkDeletePages = async () => {
    if (selectedBatchPageIds.size === 0) return;
    const count = selectedBatchPageIds.size;
    const confirmMsg = `Biztosan törölni szeretné a kijelölt ${count} db menüpontot és a hozzájuk tartozó összes rekordot és meződefiníciót?\n\nAz oldalak a teljes tartalmukkal a Lomtárba kerülnek, ahonnan 30 napig visszaállíthatók.`;
    if (!confirm(confirmMsg)) return;

    setIsLoadingPages(true);
    try {
      const res = await ApiClient.bulkDeletePages(Array.from(selectedBatchPageIds));
      if (res.ok) {
        if (selectedPageId && selectedBatchPageIds.has(selectedPageId)) {
          setSelectedPageId(null);
        }
        setSelectedBatchPageIds(new Set());
        await loadPages();
      } else {
        alert(res.error || 'Hiba történt a csoportos törlés során');
      }
    } catch (err: any) {
      alert(err?.message || 'Hálózati hiba a csoportos törlés során');
    } finally {
      setIsLoadingPages(false);
    }
  };

  const handleOpenInRecords = () => {
    if (!selectedPageId) return;
    setActivePageId(selectedPageId);
    setActiveCategory('transparency_data');
    setActiveModule('records_manager');
  };

  // Auto slug generation for new page
  const handleNewPageTitleChange = (val: string) => {
    setNewPageTitle(val);
    const autoSlug = val
      .toLowerCase()
      .replace(/[áéíóöőúüű]/g, m => ({ á: 'a', é: 'e', í: 'i', ó: 'o', ö: 'o', ő: 'o', ú: 'u', ü: 'u', ű: 'u' }[m] || m))
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40);
    setNewPageSlug(autoSlug);
  };

  const handleCreatePage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPageTitle.trim() || !newPageSlug.trim()) {
      alert('A cím és a slug megadása kötelező!');
      return;
    }

    setIsCreatingPage(true);
    const res = await ApiClient.createPage({
      title: newPageTitle.trim(),
      slug: newPageSlug.trim(),
      description: newPageDesc.trim(),
      icon: newPageIcon,
    });

    // B2: a POST /api/pages válasza { ok, id } (nincs `data` boríték).
    const newPageId = res.data?.id ?? res.id;
    if (res.ok && newPageId) {
      setIsNewPageModalOpen(false);
      setNewPageTitle('');
      setNewPageSlug('');
      setNewPageDesc('');
      setSelectedPageId(newPageId);
      await loadPages();
    } else {
      alert(res.error || 'Hiba az oldal létrehozásakor');
    }
    setIsCreatingPage(false);
  };

  // Auto field name generation
  const handleNewFieldLabelChange = (val: string) => {
    setNewFieldLabel(val);
    const autoName = val
      .toLowerCase()
      .replace(/[áéíóöőúüű]/g, m => ({ á: 'a', é: 'e', í: 'i', ó: 'o', ö: 'o', ő: 'o', ú: 'u', ü: 'u', ű: 'u' }[m] || m))
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 30);
    setNewFieldName(autoName);
  };

  const handleCreateField = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPageId) return;
    if (!newFieldLabel.trim() || !newFieldName.trim()) {
      alert('A felirat és az azonosító kötelező!');
      return;
    }

    setIsCreatingField(true);
    let optionsJson: string | null = null;
    if (newFieldType === 'select' && newFieldOptions.trim()) {
      const parsedOptions = newFieldOptions
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean);
      optionsJson = JSON.stringify(parsedOptions);
    }

    const res = await ApiClient.createField(selectedPageId, {
      label: newFieldLabel.trim(),
      name: newFieldName.trim(),
      type: newFieldType,
      placeholder: newFieldPlaceholder.trim() || null,
      required: newFieldRequired ? 1 : 0,
      options: optionsJson,
    });

    if (res.ok) {
      setIsNewFieldModalOpen(false);
      setNewFieldLabel('');
      setNewFieldName('');
      setNewFieldPlaceholder('');
      setNewFieldRequired(false);
      setNewFieldOptions('');
      loadFields(selectedPageId);
    } else {
      alert(res.error || 'Hiba a mező hozzáadásakor');
    }
    setIsCreatingField(false);
  };

  const handleToggleFieldPublic = async (fieldId: number, currentVal: number | undefined) => {
    const newVal = currentVal !== 0 ? 0 : 1;
    // Optimistic update
    setFields(prev => prev.map(f => f.id === fieldId ? { ...f, public_visible: newVal } : f));
    await ApiClient.updateField(fieldId, { public_visible: newVal });
  };

  const handleUpdateFieldPublicLabel = async (fieldId: number, val: string) => {
    await ApiClient.updateField(fieldId, { public_label: val.trim() || null });
  };

  const handleDeleteField = async (fieldId: number, fieldLabel: string) => {
    if (!confirm(`Biztosan törli a(z) "${fieldLabel}" meződefiníciót? A meglévő rekordok adata megmarad, de a definíció törlődik.`)) {
      return;
    }
    setFields(prev => prev.filter(f => f.id !== fieldId));
    await ApiClient.deleteField(fieldId);
  };

  return (
    <div className="space-y-6 pb-12 animate-fade-in">
      {/* Fejléc */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-indigo-600 dark:text-cyan-400">
            <Sliders className="w-4 h-4" />
            <span>KÖZZÉTÉTELI STRUKTÚRA & OLDALADMINISZTRÁCIÓ</span>
          </div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white mt-1">
            Oldalak és Mező Definíciók
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Testreszabhatja a kötelező közadat-oldalakat, vagy tetszőleges új publikus oldalakat és egyedi űrlapmezőket hozhat létre.
          </p>
        </div>

        <div className="flex items-center gap-2 self-start md:self-auto">
        <button
          type="button"
          onClick={() => setTrashOpen(true)}
          className="px-3 py-2 rounded-xl bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer"
          title="Törölt oldalak, bejegyzések és mellékletek visszaállítása (30 napig)"
        >
          <Trash2 className="w-4 h-4 text-rose-500" />
          <span>Lomtár</span>
        </button>
        <button
          type="button"
          onClick={() => setArchiveOpen(true)}
          className="px-3 py-2 rounded-xl bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer"
          title="A jogszabály szerint megőrzendő (archivált) tételek – megőrzési idővel, teljes letöltéssel"
        >
          <Archive className="w-4 h-4 text-amber-600" />
          <span>Archívum</span>
        </button>
        <button
          onClick={() => setIsNewPageModalOpen(true)}
          className="px-3.5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold flex items-center gap-1.5 transition shadow-sm cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          <span>Új oldal létrehozása</span>
        </button>
        </div>
      </div>
      <TrashModal isOpen={trashOpen} onClose={() => setTrashOpen(false)} onRestored={() => loadPages()} />
      <ArchiveModal isOpen={archiveOpen} onClose={() => setArchiveOpen(false)} onRestored={() => loadPages()} />
      <ArchiveDialog isOpen={Boolean(archiveTarget)} kind="page" ids={archiveTarget?.ids || []} label={archiveTarget?.label}
        onClose={() => setArchiveTarget(null)}
        onDone={async () => {
          const ids = new Set(archiveTarget?.ids || []);
          setSelectedBatchPageIds((prev) => new Set([...prev].filter((x) => !ids.has(x))));
          if (selectedPageId && ids.has(selectedPageId)) setSelectedPageId(null);
          await loadPages();
        }} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* BAL OSZLOP: Oldalak listája (1. kép alapján) */}
        <div className="lg:col-span-4 space-y-3">
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              {deletablePages.length > 0 && (
                <label className="flex items-center gap-1 text-slate-600 dark:text-slate-400 cursor-pointer select-none" title="Összes törölhető oldal kijelölése">
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 rounded border-slate-300 dark:border-slate-700 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    checked={deletablePages.length > 0 && deletablePages.every(p => selectedBatchPageIds.has(p.id))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedBatchPageIds(new Set(deletablePages.map(p => p.id)));
                      } else {
                        setSelectedBatchPageIds(new Set());
                      }
                    }}
                  />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Mind</span>
                </label>
              )}
              <span className="text-[11px] font-mono font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase">
                OLDALAK
              </span>
            </div>

            <div className="flex items-center gap-2">
              {selectedBatchPageIds.size > 0 && (
                <button
                  onClick={handleBulkDeletePages}
                  className="flex items-center gap-1 px-2 py-1 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/50 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800/60 rounded-lg text-[11px] font-semibold transition cursor-pointer"
                  title="Kijelölt oldalak csoportos törlése"
                >
                  <Trash2 className="w-3 h-3" />
                  <span>Törlés ({selectedBatchPageIds.size})</span>
                </button>
              )}
              {selectedBatchPageIds.size > 0 && (
                <button
                  onClick={() => setArchiveTarget({ ids: [...selectedBatchPageIds].filter((id) => !pages.find((p) => p.id === id)?.is_fixed) })}
                  className="flex items-center gap-1 px-2 py-1 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/50 dark:hover:bg-amber-900/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60 rounded-lg text-[11px] font-semibold transition cursor-pointer"
                  title="Kijelölt oldalak archiválása a teljes tartalmukkal (a jogszabályi megőrzési idő kijelzésével)"
                >
                  <Archive className="w-3 h-3" />
                  <span>Archiválás ({selectedBatchPageIds.size})</span>
                </button>
              )}
              <span className="text-[10px] font-mono text-slate-400">{pages.length} oldal</span>
            </div>
          </div>

          <div className="bg-white dark:bg-[#0b0f1a] border border-slate-200 dark:border-slate-800 rounded-2xl p-2.5 shadow-xs space-y-1 overflow-hidden">
            {isLoadingPages ? (
              <div className="py-8 text-center text-xs text-slate-400">
                <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-1 text-indigo-500" />
                Oldalak betöltése...
              </div>
            ) : pages.length === 0 ? (
              <div className="py-6 text-center text-xs text-slate-500">Nincsenek oldalak</div>
            ) : (
              pages.map((p) => {
                const isSelected = p.id === selectedPageId;
                const isPub = p.published === 1;

                return (
                  <button
                    key={p.id}
                    onClick={() => selectPage(p)}
                    className={`w-full flex items-center justify-between p-2.5 rounded-xl text-left transition cursor-pointer text-xs ${
                      isSelected
                        ? 'bg-indigo-50/80 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800/80 font-medium text-slate-900 dark:text-white shadow-xs'
                        : 'border border-transparent hover:bg-slate-50 dark:hover:bg-slate-900/60 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0 pr-2">
                      {!p.is_fixed ? (
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 rounded border-slate-300 dark:border-slate-700 text-indigo-600 focus:ring-indigo-500 shrink-0 cursor-pointer"
                          checked={selectedBatchPageIds.has(p.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e.stopPropagation();
                            setSelectedBatchPageIds(prev => {
                              const next = new Set(prev);
                              if (next.has(p.id)) next.delete(p.id);
                              else next.add(p.id);
                              return next;
                            });
                          }}
                        />
                      ) : (
                        <span className="w-3.5 shrink-0" />
                      )}
                      <span className="text-base select-none shrink-0">{p.icon || '📋'}</span>
                      <span className="truncate">{p.title}</span>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {p.active === 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-300 dark:border-rose-800/60">
                          inaktív
                        </span>
                      )}

                      {(p.record_count ?? 0) > 0 ? (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60" title={`${p.record_count} rögzített rekord`}>
                          {p.record_count} db
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400" title="Üres oldal (0 rekord)">
                          0 db
                        </span>
                      )}

                      {isPub ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800/60">
                          közzétéve
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300 dark:border-amber-800/60">
                          rejtett
                        </span>
                      )}

                      {p.is_fixed ? (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                          fix
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* JOBB OSZLOP: Oldal szerkesztése & Mezők (2. és 3. kép alapján) */}
        <div className="lg:col-span-8 space-y-6">
          {!selectedPage ? (
            <div className="p-12 text-center text-slate-400 bg-white dark:bg-[#0b0f1a] border border-slate-200 dark:border-slate-800 rounded-2xl">
              ← Válasszon egy oldalt a bal oldali listából a szerkesztéshez
            </div>
          ) : (
            <>
              {/* 2. KÉP: Oldal Meta Szerkesztése */}
              <div className="space-y-2">
                <div className="flex items-center justify-between px-1">
                  <span className="text-[11px] font-mono font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase">
                    SZERKESZTÉS: {selectedPage.title}
                  </span>
                </div>

                <div className="bg-white dark:bg-[#0b0f1a] border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs space-y-4">
                  {/* Felső sáv ikonnal és műveleti gombokkal */}
                  <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xl select-none">
                        {icon}
                      </div>
                      <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                          <span>{title || selectedPage.title}</span>
                          {selectedPage.is_fixed ? (
                            <span className="text-[10px] font-mono bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 px-1.5 py-0.5 rounded">
                              jogszabályi törzsoldal
                            </span>
                          ) : null}
                          {!active && (
                            <span className="text-[10px] font-medium bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 px-1.5 py-0.5 rounded border border-rose-200 dark:border-rose-900">
                              inaktív
                            </span>
                          )}
                        </h2>
                        <p className="text-[11px] font-mono text-slate-400">/{slug}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setIsPreviewModalOpen(true)}
                        className="px-2.5 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-medium flex items-center gap-1 transition cursor-pointer"
                        title="Publikus nézet előnézete"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>Előnézet</span>
                      </button>

                      <button
                        onClick={handleOpenInRecords}
                        className="px-2.5 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 text-xs font-medium flex items-center gap-1 transition cursor-pointer"
                        title="Rekordok megnyitása az Adatkezelőben"
                      >
                        <ArrowRight className="w-3.5 h-3.5" />
                        <span>Megnyitás</span>
                      </button>

                      {!selectedPage.is_fixed && (
                        <button
                          onClick={() => setArchiveTarget({ ids: [selectedPage.id], label: selectedPage.title })}
                          className="px-2.5 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 text-xs font-medium flex items-center gap-1 transition cursor-pointer"
                          title="Oldal archiválása a teljes tartalmával (a jogszabályi megőrzési idő kijelzésével)"
                        >
                          <Archive className="w-3.5 h-3.5" />
                          <span>Archiválás</span>
                        </button>
                      )}

                      {!selectedPage.is_fixed && (
                        <button
                          onClick={handleDeletePage}
                          className="px-2.5 py-1.5 rounded-lg bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/40 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 text-xs font-medium flex items-center gap-1 transition cursor-pointer"
                          title="Oldal és kapcsolódó rekordok törlése"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Törlés</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Űrlapmezők */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-mono uppercase text-slate-600 dark:text-slate-400 mb-1">
                        CÍM
                      </label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-mono uppercase text-slate-600 dark:text-slate-400 mb-1">
                        SLUG (URL)
                      </label>
                      <input
                        type="text"
                        value={slug}
                        readOnly
                        className="w-full bg-slate-100 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-500 font-mono cursor-not-allowed"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-[11px] font-mono uppercase text-slate-600 dark:text-slate-400 mb-1">
                        LEÍRÁS
                      </label>
                      <input
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Rövid tájékoztató szöveg a közzétételi oldalról"
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    {/* Ikonválasztó */}
                    <div className="md:col-span-2 space-y-1.5">
                      <label className="block text-[11px] font-mono uppercase text-slate-600 dark:text-slate-400">
                        IKON
                      </label>
                      <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-xl">
                        {ICON_PRESETS.map((ico) => (
                          <button
                            key={ico}
                            type="button"
                            onClick={() => setIcon(ico)}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm transition cursor-pointer ${
                              icon === ico
                                ? 'bg-indigo-600 text-white shadow-xs scale-105'
                                : 'bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700'
                            }`}
                          >
                            {ico}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Kapcsolók */}
                    <div className="md:col-span-2 space-y-3 pt-1">
                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={(e) => setActive(e.target.checked)}
                          className="mt-0.5 rounded text-indigo-600 focus:ring-indigo-500"
                        />
                        <div>
                          <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                            Aktív (megjelenik a navigációban és elérhető)
                          </span>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                            Kikapcsolva az oldal és adatai megmaradnak, csak elrejtve a felhasználók elől.
                          </p>
                        </div>
                      </label>

                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={published}
                          onChange={(e) => setPublished(e.target.checked)}
                          className="mt-0.5 rounded text-indigo-600 focus:ring-indigo-500"
                        />
                        <div>
                          <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                            <span>Közzétéve (látszik a publikus oldalon / statikus exportban)</span>
                          </span>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                            Csak a kifejezetten közzétett oldalak jelennek meg kívülről – a piszkozat/félkész oldalak rejtve maradnak, amíg ezt be nem kapcsolja.
                          </p>
                        </div>
                      </label>
                    </div>

                    {/* Megjelenítési mód */}
                    <div className="md:col-span-2 space-y-1">
                      <label className="block text-[11px] font-mono uppercase text-slate-600 dark:text-slate-400">
                        MEGJELENÍTÉSI MÓD (PUBLIKUS OLDAL)
                      </label>
                      <select
                        value={displayMode}
                        onChange={(e) => setDisplayMode(e.target.value as any)}
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="accordion">Kinyíló listás nézet (harmonika, többszintű - Alapértelmezett)</option>
                        <option value="cards">Kártyás lista (blokkos nézet)</option>
                        <option value="table">Táblázat (oszlopos megjelenítés)</option>
                        <option value="list">Lenyíló lista (egyszerű, csoportosítás nélkül)</option>
                      </select>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                        <b>Kinyíló lista (harmonika):</b> többszintű hierarchia (főpont, alpont, évek) beágyazott dokumentumkártyákkal és közvetlen megnyitással. <b>Kártyás:</b> egyszerű kártyás blokkok. <b>Táblázat:</b> klasszikus oszlopos táblázat.
                      </p>
                    </div>
                  </div>

                  {/* Mentés gomb és visszajelzés */}
                  <div className="flex items-center gap-3 pt-2">
                    <button
                      onClick={handleSaveMeta}
                      disabled={isSavingMeta}
                      className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1.5 transition shadow-sm cursor-pointer"
                    >
                      {isSavingMeta ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      <span>{isSavingMeta ? 'Mentés folyamatban...' : '💾 Mentés'}</span>
                    </button>

                    {successMessage && (
                      <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5" />
                        <span>{successMessage}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* 3. KÉP: Mező Definíciók Szerkesztése */}
              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-between px-1">
                  <span className="text-[11px] font-mono font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase">
                    MEZŐK
                  </span>
                </div>

                <div className="bg-white dark:bg-[#0b0f1a] border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-2">
                      <span className="text-base select-none">📝</span>
                      <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                        Mező definíciók
                      </h3>
                      <span className="text-[11px] font-mono text-slate-400">({fields.length} db mező)</span>
                    </div>

                    <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        if (!selectedPageId) return;
                        setFieldSyncMsg(null);
                        const r = await ApiClient.syncTemplateFields(selectedPageId);
                        if (r.ok) {
                          setFieldSyncMsg(r.added?.length ? `Pótolva: ${r.added.length} mező (${r.added.join(', ')})` : 'A sablon minden mezője megvan.');
                          loadFields(selectedPageId);
                        } else setFieldSyncMsg(r.error || 'Az igazítás nem sikerült');
                      }}
                      title="A közzétételi lista mezőinek pótlása (pont, cím, leírás, külső linkek, frissítés, megőrzés) – a meglévő mezők nem változnak"
                      className="px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 text-xs font-medium flex items-center gap-1 transition cursor-pointer"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      <span>Igazítás a sablonhoz</span>
                    </button>
                    <button
                      onClick={() => setIsNewFieldModalOpen(true)}
                      className="px-3 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 text-xs font-medium flex items-center gap-1 transition cursor-pointer"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>＋ Mező</span>
                    </button>
                    </div>
                  </div>
                  {fieldSyncMsg && <p role="status" className="text-[11px] text-emerald-700 dark:text-emerald-400">{fieldSyncMsg}</p>}

                  {/* Mezők listája */}
                  {isLoadingFields ? (
                    <div className="py-8 text-center text-xs text-slate-400">
                      <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-1 text-indigo-500" />
                      Mezők betöltése...
                    </div>
                  ) : fields.length === 0 ? (
                    <div className="py-8 text-center text-xs text-slate-500">
                      Még nincs mező definiálva ehhez az oldalhoz. A közzétételi lista mezőit az <b>Igazítás a sablonhoz</b> gomb pótolja, saját mezőt a <b>＋ Mező</b> gombbal adhat hozzá.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {fields.map((f) => {
                        const isPublic = f.public_visible !== 0;

                        return (
                          <div
                            key={f.id}
                            className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 space-y-2"
                          >
                            {/* Első sor: Címke, belső név, típus, kötelező badge, törlés */}
                            <div className="flex items-center gap-2.5">
                              <span className="text-slate-400 select-none cursor-grab">
                                <GripVertical className="w-4 h-4" />
                              </span>

                              <div className="flex-1 flex items-center gap-2 min-w-0">
                                <span className="font-semibold text-xs text-slate-900 dark:text-slate-100 truncate">
                                  {f.label}
                                </span>
                                <span className="font-mono text-[11px] text-slate-400 truncate">
                                  {f.name}
                                </span>
                              </div>

                              <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-slate-200/70 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                {f.type}
                              </span>

                              {f.required ? (
                                <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-rose-900/90 text-rose-100">
                                  kötelező
                                </span>
                              ) : null}

                              <button
                                onClick={() => handleDeleteField(f.id, f.label)}
                                className="p-1 rounded text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-950/60 transition cursor-pointer"
                                title="Mező törlése"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>

                            {/* Második sor: Publikus kapcsoló és publikus felirat */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 pl-6 pt-1 border-t border-slate-100 dark:border-slate-800/80">
                              <label
                                className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 cursor-pointer shrink-0 select-none"
                                title="Látszik-e ez a mező a publikus oldalon / statikus exportban"
                              >
                                <input
                                  type="checkbox"
                                  checked={isPublic}
                                  onChange={() => handleToggleFieldPublic(f.id, f.public_visible)}
                                  className="rounded text-indigo-600 focus:ring-indigo-500"
                                />
                                <Globe className="w-3 h-3 text-indigo-500" />
                                <span className="font-medium">Publikus</span>
                              </label>

                              <div className="flex-1">
                                <input
                                  type="text"
                                  defaultValue={f.public_label || ''}
                                  placeholder={`Publikus felirat (üresen: ${f.label})`}
                                  onBlur={(e) => handleUpdateFieldPublicLabel(f.id, e.target.value)}
                                  className="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg px-2.5 py-1 text-xs text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                  title="Ha ki van töltve, ez a felirat jelenik meg a publikus felületen az eredeti mezőnév helyett"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ÚJ OLDAL MODAL */}
      {isNewPageModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-xs animate-fade-in overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md shadow-2xl my-auto max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0">
              <span className="font-bold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                <Plus className="w-4 h-4 text-indigo-600" />
                <span>Új oldal létrehozása</span>
              </span>
              <button
                onClick={() => setIsNewPageModalOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCreatePage} className="p-5 space-y-3.5 overflow-y-auto pr-2 flex-1">
              <div>
                <label className="block text-[11px] font-mono uppercase text-slate-600 dark:text-slate-400 mb-1">
                  Cím *
                </label>
                <input
                  type="text"
                  required
                  placeholder="pl. Határozatok és Rendeletek"
                  value={newPageTitle}
                  onChange={(e) => handleNewPageTitleChange(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono uppercase text-slate-600 dark:text-slate-400 mb-1">
                  Slug (URL) *
                </label>
                <input
                  type="text"
                  required
                  placeholder="pl. hatarozatok"
                  value={newPageSlug}
                  onChange={(e) => setNewPageSlug(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs font-mono text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono uppercase text-slate-600 dark:text-slate-400 mb-1">
                  Leírás
                </label>
                <input
                  type="text"
                  placeholder="Rövid leírás az oldal céljáról"
                  value={newPageDesc}
                  onChange={(e) => setNewPageDesc(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono uppercase text-slate-600 dark:text-slate-400 mb-1">
                  Ikon
                </label>
                <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
                  {ICON_PRESETS.map((ico) => (
                    <button
                      key={ico}
                      type="button"
                      onClick={() => setNewPageIcon(ico)}
                      className={`w-7 h-7 rounded flex items-center justify-center text-xs transition cursor-pointer ${
                        newPageIcon === ico
                          ? 'bg-indigo-600 text-white shadow-xs'
                          : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      {ico}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsNewPageModalOpen(false)}
                  className="px-3.5 py-1.5 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 text-xs hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                >
                  Mégsem
                </button>
                <button
                  type="submit"
                  disabled={isCreatingPage}
                  className="px-4 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold shadow-xs transition cursor-pointer flex items-center gap-1.5"
                >
                  {isCreatingPage ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : null}
                  <span>Létrehozás</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ÚJ MEZŐ MODAL */}
      {isNewFieldModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-xs animate-fade-in overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md shadow-2xl my-auto max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0">
              <span className="font-bold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                <Plus className="w-4 h-4 text-indigo-600" />
                <span>Új mező hozzáadása</span>
              </span>
              <button
                onClick={() => setIsNewFieldModalOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCreateField} className="p-5 space-y-3.5 overflow-y-auto pr-2 flex-1">
              <div>
                <label className="block text-[11px] font-mono uppercase text-slate-600 dark:text-slate-400 mb-1">
                  Felirat *
                </label>
                <input
                  type="text"
                  required
                  placeholder="pl. Határozat száma"
                  value={newFieldLabel}
                  onChange={(e) => handleNewFieldLabelChange(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono uppercase text-slate-600 dark:text-slate-400 mb-1">
                  Belső azonosító (name) *
                </label>
                <input
                  type="text"
                  required
                  placeholder="pl. hatarozat_szama"
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs font-mono text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono uppercase text-slate-600 dark:text-slate-400 mb-1">
                  Típus
                </label>
                <select
                  value={newFieldType}
                  onChange={(e) => setNewFieldType(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="text">Egysoros szöveg (text)</option>
                  <option value="textarea">Többsoros szöveg (textarea)</option>
                  <option value="select">Lenyíló lista (select)</option>
                  <option value="date">Dátum (date)</option>
                  <option value="email">E-mail cím</option>
                  <option value="tel">Telefonszám</option>
                  <option value="url">Weboldal hivatkozás (URL)</option>
                </select>
              </div>

              {newFieldType === 'select' && (
                <div>
                  <label className="block text-[11px] font-mono uppercase text-slate-600 dark:text-slate-400 mb-1">
                    Választható opciók (soronként egy érték)
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Opció 1&#10;Opció 2&#10;Opció 3"
                    value={newFieldOptions}
                    onChange={(e) => setNewFieldOptions(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-[11px] font-mono uppercase text-slate-600 dark:text-slate-400 mb-1">
                  Helyőrző szöveg (placeholder)
                </label>
                <input
                  type="text"
                  placeholder="pl. Írja be ide..."
                  value={newFieldPlaceholder}
                  onChange={(e) => setNewFieldPlaceholder(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="pt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newFieldRequired}
                    onChange={(e) => setNewFieldRequired(e.target.checked)}
                    className="rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                    Kötelezően kitöltendő mező
                  </span>
                </label>
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsNewFieldModalOpen(false)}
                  className="px-3.5 py-1.5 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 text-xs hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                >
                  Mégsem
                </button>
                <button
                  type="submit"
                  disabled={isCreatingField}
                  className="px-4 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold shadow-xs transition cursor-pointer flex items-center gap-1.5"
                >
                  {isCreatingField ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : null}
                  <span>Létrehozás</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ELŐNÉZET MODAL */}
      {isPreviewModalOpen && selectedPage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-xs animate-fade-in overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl my-auto overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="text-xl">{icon}</span>
                <div>
                  <h3 className="font-bold text-sm text-slate-900 dark:text-white">
                    {title} – Publikus Előnézet
                  </h3>
                  <span className="text-[10px] text-slate-400 font-mono">
                    Mód: {displayMode} | {published ? 'Közzétéve' : 'Rejtett'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setIsPreviewModalOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4">
              <div className="p-4 rounded-xl bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-200/60 dark:border-indigo-800/40">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{icon}</span>
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white text-base">{title}</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{description || '–'}</p>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="text-xs font-mono font-bold uppercase text-slate-500 mb-2">
                  Publikus mezők listája
                </h5>
                <div className="space-y-1.5">
                  {fields.filter(f => f.public_visible !== 0).map(f => (
                    <div key={f.id} className="p-2.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex items-center justify-between text-xs">
                      <span className="font-medium text-slate-800 dark:text-slate-200">
                        {f.public_label || f.label}
                      </span>
                      <span className="text-[10px] font-mono text-slate-400">{f.type}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-3 border-t border-slate-200 dark:border-slate-800 flex justify-end">
              <button
                onClick={() => setIsPreviewModalOpen(false)}
                className="px-4 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-semibold hover:bg-slate-200 dark:hover:bg-slate-700 transition cursor-pointer"
              >
                Bezárás
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
